import { getReferencesFromRawJson } from '@apps/web/components/phoebe_chat/event_transcript';
import {
    type StreamRunNode,
    type StreamRunState,
} from '@apps/web/components/phoebe_chat/streaming_types';
import { shouldRenderToolEntryTopLevel } from '@apps/web/components/phoebe_chat_view/tool_calls/tool_entry_predicates';
import { type AgentTranscriptSource } from '@apps/web/components/phoebe_chat_view/transcript/legacy_transcript';
import {
    buildToolExecutionEntry,
    extractReasoning,
} from '@apps/web/components/phoebe_chat_view/transcript/streaming_transcript';
import {
    type ToolAwareItem,
    type ToolExecutionEntry,
} from '@apps/web/components/phoebe_chat_view/transcript_types';

export function buildStreamRunStateFromPersistedTurns(
    runId: string,
    requestTurnId: string | null,
    conversation: AgentTranscriptSource,
    startedAt: string,
): StreamRunState {
    const nodesById: Record<string, StreamRunNode> = {};
    const orderedNodeIds: Array<string> = [];
    let sequence = -1000;

    const pushNode = (node: Omit<StreamRunNode, 'sequence'>): StreamRunNode => {
        const nextNode: StreamRunNode = {
            ...node,
            sequence,
        };
        nodesById[nextNode.id] = nextNode;
        orderedNodeIds.push(nextNode.id);
        sequence += 1;
        return nextNode;
    };

    const pushToolNode = (
        toolEntry: ToolExecutionEntry,
        callItem: ToolAwareItem,
        parentNodeId: string | null,
    ) => {
        pushNode({
            id: toolEntry.callId ? `tool:${toolEntry.callId}` : callItem.id,
            parentNodeId,
            kind: 'tool',
            createdAt: toolEntry.createdAt,
            content: '',
            phase: null,
            label: null,
            detail: null,
            toolCallId: toolEntry.callId,
            toolName: toolEntry.name,
            arguments: toolEntry.arguments,
            result: toolEntry.output ?? toolEntry.text,
            error: toolEntry.error ?? null,
            status: toolEntry.status ?? 'completed',
            isComplete: toolEntry.isComplete,
            durationMs: toolEntry.durationMs ?? null,
            responseKind: null,
        });
    };

    const requestTurnSequence =
        requestTurnId === null
            ? null
            : (conversation.turns.find((turn) => turn.id === requestTurnId)
                  ?.sequence ?? null);
    if (requestTurnSequence === null) {
        return {
            runId,
            requestTurnId,
            startedAt,
            status: 'running',
            nodesById,
            orderedNodeIds,
        };
    }

    const turnsAfterRequest = conversation.turns
        .filter((turn) => turn.sequence > requestTurnSequence)
        .sort((a, b) => a.sequence - b.sequence);

    turnsAfterRequest.forEach((turn) => {
        const orderedItems = [...turn.items].sort((a, b) => {
            const createdAtCompare = a.created_at.localeCompare(b.created_at);
            if (createdAtCompare !== 0) {
                return createdAtCompare;
            }
            const indexCompare = a.index_in_turn - b.index_in_turn;
            if (indexCompare !== 0) {
                return indexCompare;
            }
            return a.id.localeCompare(b.id);
        });
        const hasFunctionCallAfterTextItemId = new Map<string, boolean>();
        let hasFunctionCallAfter = false;
        for (let index = orderedItems.length - 1; index >= 0; index -= 1) {
            const orderedItem = orderedItems[index];
            if (!orderedItem) {
                continue;
            }
            if (orderedItem.type === 'function_call') {
                hasFunctionCallAfter = true;
                continue;
            }
            if (orderedItem.type === 'text') {
                hasFunctionCallAfterTextItemId.set(
                    orderedItem.id,
                    hasFunctionCallAfter,
                );
            }
        }

        const toolExecutions = new Map<
            string,
            {
                call: ToolAwareItem;
                result?: ToolAwareItem;
            }
        >();
        orderedItems.forEach((item) => {
            const toolItem = item as ToolAwareItem;
            if (item.type === 'function_call' && toolItem.call_id) {
                toolExecutions.set(toolItem.call_id, {
                    call: toolItem,
                    result: toolExecutions.get(toolItem.call_id)?.result,
                });
                return;
            }
            if (item.type === 'function_output' && toolItem.call_id) {
                const existing = toolExecutions.get(toolItem.call_id);
                if (existing) {
                    toolExecutions.set(toolItem.call_id, {
                        ...existing,
                        result: toolItem,
                    });
                } else {
                    toolExecutions.set(toolItem.call_id, {
                        call: toolItem,
                        result: toolItem,
                    });
                }
            }
        });

        const callEntries = new Map<
            string,
            { entry: ToolExecutionEntry; callItem: ToolAwareItem }
        >();
        toolExecutions.forEach((execution, callId) => {
            callEntries.set(callId, {
                entry: buildToolExecutionEntry(
                    execution.call,
                    execution.result,
                    callId,
                    turn.sequence,
                ),
                callItem: execution.call,
            });
        });

        const attachedCallIds = new Set<string>();
        let currentThoughtNodeId: string | null = null;
        let latestThoughtNodeId: string | null = null;

        orderedItems.forEach((item) => {
            const toolItem = item as ToolAwareItem;

            switch (item.type) {
                case 'text': {
                    if (
                        item.direction !== 'output' ||
                        item.role !== 'assistant'
                    ) {
                        if (item.direction === 'input') {
                            currentThoughtNodeId = null;
                        }
                        break;
                    }

                    const content = (item.text_content ?? '').trim();
                    if (content.length === 0) {
                        break;
                    }
                    const references = getReferencesFromRawJson(item.raw_json);

                    pushNode({
                        id: item.id,
                        parentNodeId: null,
                        kind: 'response',
                        createdAt: item.created_at,
                        content,
                        phase: null,
                        label: null,
                        detail: null,
                        toolCallId: null,
                        toolName: null,
                        arguments: null,
                        result: null,
                        error: null,
                        status: 'completed',
                        isComplete: true,
                        responseKind: hasFunctionCallAfterTextItemId.get(
                            item.id,
                        )
                            ? 'interstitial'
                            : null,
                        references,
                    });
                    break;
                }
                case 'function_call': {
                    const callId = toolItem.call_id;
                    if (!callId || attachedCallIds.has(callId)) {
                        break;
                    }

                    const toolEntry = callEntries.get(callId);
                    if (!toolEntry) {
                        break;
                    }

                    attachedCallIds.add(callId);
                    const shouldRenderTopLevel = shouldRenderToolEntryTopLevel(
                        toolEntry.entry,
                    );
                    const thoughtTarget =
                        toolEntry.entry.name === 'monitor_outreach'
                            ? null
                            : (currentThoughtNodeId ?? latestThoughtNodeId);
                    pushToolNode(
                        toolEntry.entry,
                        toolEntry.callItem,
                        thoughtTarget && !shouldRenderTopLevel
                            ? thoughtTarget
                            : null,
                    );
                    break;
                }
                case 'function_output':
                    break;
                case 'reasoning': {
                    const content = extractReasoning(toolItem);
                    if (!content) {
                        break;
                    }
                    const references = getReferencesFromRawJson(item.raw_json);
                    const thoughtNode = pushNode({
                        id: item.id,
                        parentNodeId: null,
                        kind: 'reasoning',
                        createdAt: item.created_at,
                        content,
                        phase: null,
                        label: null,
                        detail: null,
                        toolCallId: null,
                        toolName: null,
                        arguments: null,
                        result: null,
                        error: null,
                        status: 'completed',
                        isComplete: true,
                        responseKind: null,
                        references,
                    });
                    currentThoughtNodeId = thoughtNode.id;
                    latestThoughtNodeId = thoughtNode.id;
                    break;
                }
                default:
                    break;
            }
        });

        callEntries.forEach((toolEntry, callId) => {
            if (attachedCallIds.has(callId)) {
                return;
            }
            pushToolNode(toolEntry.entry, toolEntry.callItem, null);
        });
    });

    return {
        runId,
        requestTurnId,
        startedAt,
        status: 'running',
        nodesById,
        orderedNodeIds,
    };
}
